import { useQuery } from "@tanstack/react-query";
import { api, fmtCurrencyPrecise } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

const INFLOW = ["Deposit", "Dividend", "Sell"];
const BADGE = {
  Deposit: "bg-emerald-50 text-emerald-700",
  Dividend: "bg-blue-50 text-blue-700",
  Withdrawal: "bg-red-50 text-red-700",
  Buy: "bg-slate-100 text-slate-700",
  Sell: "bg-amber-50 text-amber-700",
};

export default function Transactions() {
  const { data, isLoading } = useQuery({
    queryKey: ["transactions"],
    queryFn: async () => (await api.get("/transactions/me")).data,
  });

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="fade-up">
        <p className="overline mb-1">Activity</p>
        <h1 className="font-serif text-4xl text-[#0f172a]">Transaction History</h1>
      </div>

      <Card className="bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] overflow-hidden fade-up">
        {isLoading && (
          <div className="p-6 space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        )}
        {!isLoading && (data || []).length === 0 && (
          <div className="p-12 text-center text-slate-500">No transactions recorded yet.</div>
        )}
        {!isLoading && (data || []).length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f8f9fa] hover:bg-[#f8f9fa]">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((t, i) => {
                const inflow = INFLOW.includes(t.type);
                return (
                  <TableRow key={t.id || i} data-testid={`txn-row-${i}`} className="hover:bg-slate-50 transition-colors duration-200">
                    <TableCell className="text-slate-600 tabular-nums">{t.date}</TableCell>
                    <TableCell><Badge className={`${BADGE[t.type]} border-0 font-medium`}>{t.type}</Badge></TableCell>
                    <TableCell className="text-slate-700">{t.description}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${inflow ? "text-[#059669]" : "text-[#dc2626]"}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {inflow ? <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} /> : <ArrowDownRight className="w-3.5 h-3.5" strokeWidth={2} />}
                        {inflow ? "+" : "-"}{fmtCurrencyPrecise(t.amount)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
